import { convertChannel } from './converters'
import type {
  ChatServerChannel,
  ChatServerMessage,
  ChatServerParticipant,
  ChatServerPresence,
  ChatServerPresenceDiff,
  ChatServerReaction,
  ChatServerTyping,
  ResolvedParticipant,
} from './types'

/** Typing marker lifetime. */
export const TYPING_TTL_MS = 5_000

export type ChatServerChannelsStatus = 'idle' | 'loading' | 'loaded' | 'error'

/**
 * The whole normalized chat_server state as one value. Hosts mount it wherever
 * they want — the web front keeps it under a single redux slice, a classic
 * store mounts `chatServerReducer` via `combineReducers`.
 */
export type ChatServerState = {
  /**
   * chat_server uid of the user this cache belongs to. Everything below is
   * user-scoped, so a change wipes it — see `setOwner`.
   */
  ownerUid: string | null
  channels: Record<string, ChatServerChannel>
  participants: Record<string, ChatServerParticipant>
  /** Messages keyed by channel id → (message id → message). */
  messages: Record<string, Record<string, ChatServerMessage>>
  /** Global online of the user's dialog partners (user:* topic), keyed by user_id — feeds the dialog list. */
  globalPresence: ChatServerPresence
  /** Typing markers keyed by channel id → (user id → expiry epoch ms). */
  typing: Record<string, ChatServerTyping>
  /** Resolved participant identities keyed by external_id (see resolve endpoint). */
  resolved: Record<string, ResolvedParticipant>
  /** Per-channel unread counts keyed by channel id. */
  unread: Record<string, number>
  connected: boolean
  ready: boolean
  /** The last attempt to open the socket failed and the next one has not succeeded yet. */
  connectFailed: boolean
  /** Channels-listing load status (mirrors chat-package channelsStatus). */
  channelsStatus: ChatServerChannelsStatus
  /** Server-reported archived-channels count (`meta.archive.count` of the first channels page). */
  archiveCount: number
}

export const initialChatServerState: ChatServerState = {
  ownerUid: null,
  channels: {},
  participants: {},
  messages: {},
  globalPresence: {},
  typing: {},
  resolved: {},
  unread: {},
  connected: false,
  ready: false,
  connectFailed: false,
  channelsStatus: 'idle',
  archiveCount: 0,
}

export type RawChannelInput = Parameters<typeof convertChannel>[0]

export type ChatServerAction =
  | { type: 'chatServer/upsertChannels'; raws: RawChannelInput[] }
  | { type: 'chatServer/upsertChannel'; channel: ChatServerChannel; participants: ChatServerParticipant[] }
  | { type: 'chatServer/removeChannel'; channelId: string }
  | { type: 'chatServer/upsertMessages'; channelId: string; messages: ChatServerMessage[] }
  | { type: 'chatServer/addMessage'; message: ChatServerMessage }
  | { type: 'chatServer/editMessage'; message: ChatServerMessage }
  | { type: 'chatServer/patchMessage'; channelId: string; messageId: string; patch: Partial<ChatServerMessage> }
  | { type: 'chatServer/removeMessage'; channelId: string; messageId: string }
  | { type: 'chatServer/setMessageReactions'; channelId: string; messageId: string; reactions: ChatServerReaction[] }
  | {
      type: 'chatServer/applyReactionDelta'
      channelId: string
      messageId: string
      emoji: string
      actorExternalId: string
      direction: 'added' | 'removed'
      /** Authoritative per-emoji count from a server delta; null derives locally (optimistic toggle). */
      countAfter: number | null
    }
  | { type: 'chatServer/setUnread'; channelId: string; count: number }
  | { type: 'chatServer/incrementUnread'; channelId: string }
  | { type: 'chatServer/setUnreadBulk'; counts: Record<string, number> }
  | { type: 'chatServer/applyReadReceipt'; channelId: string; userId: string; lastReadAt: string }
  | { type: 'chatServer/setGlobalPresence'; presence: ChatServerPresence }
  | { type: 'chatServer/applyGlobalPresenceDiff'; diff: ChatServerPresenceDiff }
  | { type: 'chatServer/setTyping'; channelId: string; userId: string; expiresAt: number }
  | { type: 'chatServer/clearTyping'; channelId: string; userId: string }
  | { type: 'chatServer/upsertResolvedParticipants'; participants: ResolvedParticipant[] }
  | { type: 'chatServer/setOwner'; uid: string | null }
  | { type: 'chatServer/setConnected'; connected: boolean }
  | { type: 'chatServer/setReady'; ready: boolean }
  | { type: 'chatServer/setConnectFailed'; failed: boolean }
  | { type: 'chatServer/setChannelsStatus'; status: ChatServerChannelsStatus }
  | { type: 'chatServer/setArchiveCount'; count: number }

export const upsertChannels = (raws: RawChannelInput[]): ChatServerAction => ({ type: 'chatServer/upsertChannels', raws })

export const upsertChannel = (channel: ChatServerChannel, participants: ChatServerParticipant[] = []): ChatServerAction => ({
  type: 'chatServer/upsertChannel',
  channel,
  participants,
})

export const removeChannel = (channelId: string): ChatServerAction => ({ type: 'chatServer/removeChannel', channelId })

export const upsertMessages = (channelId: string, messages: ChatServerMessage[]): ChatServerAction => ({
  type: 'chatServer/upsertMessages',
  channelId,
  messages,
})

export const addMessage = (message: ChatServerMessage): ChatServerAction => ({ type: 'chatServer/addMessage', message })

export const editMessage = (message: ChatServerMessage): ChatServerAction => ({ type: 'chatServer/editMessage', message })

export const cancelMessage = (channelId: string, messageId: string, cancelledAt: string | null): ChatServerAction => ({
  type: 'chatServer/patchMessage',
  channelId,
  messageId,
  patch: { cancelled_at: cancelledAt },
})

export const outdateMessage = (channelId: string, messageId: string, outdatedAt: string | null): ChatServerAction => ({
  type: 'chatServer/patchMessage',
  channelId,
  messageId,
  patch: { outdated_at: outdatedAt },
})

export const removeMessage = (channelId: string, messageId: string): ChatServerAction => ({ type: 'chatServer/removeMessage', channelId, messageId })

export const setMessageReactions = (channelId: string, messageId: string, reactions: ChatServerReaction[]): ChatServerAction => ({
  type: 'chatServer/setMessageReactions',
  channelId,
  messageId,
  reactions,
})

export const applyReactionDelta = (
  channelId: string,
  messageId: string,
  emoji: string,
  actorExternalId: string,
  direction: 'added' | 'removed',
  countAfter: number | null
): ChatServerAction => ({ type: 'chatServer/applyReactionDelta', channelId, messageId, emoji, actorExternalId, direction, countAfter })

export const setUnread = (channelId: string, count: number): ChatServerAction => ({ type: 'chatServer/setUnread', channelId, count })

export const incrementUnread = (channelId: string): ChatServerAction => ({ type: 'chatServer/incrementUnread', channelId })

export const setUnreadBulk = (counts: Record<string, number>): ChatServerAction => ({ type: 'chatServer/setUnreadBulk', counts })

export const markReadOptimistic = (channelId: string): ChatServerAction => ({ type: 'chatServer/setUnread', channelId, count: 0 })

export const applyReadReceipt = (channelId: string, userId: string, lastReadAt: string): ChatServerAction => ({
  type: 'chatServer/applyReadReceipt',
  channelId,
  userId,
  lastReadAt,
})

export const setGlobalPresence = (presence: ChatServerPresence): ChatServerAction => ({ type: 'chatServer/setGlobalPresence', presence })

export const applyGlobalPresenceDiff = (diff: ChatServerPresenceDiff): ChatServerAction => ({ type: 'chatServer/applyGlobalPresenceDiff', diff })

export const setTyping = (channelId: string, userId: string, now = Date.now()): ChatServerAction => ({
  type: 'chatServer/setTyping',
  channelId,
  userId,
  expiresAt: now + TYPING_TTL_MS,
})

export const clearTyping = (channelId: string, userId: string): ChatServerAction => ({ type: 'chatServer/clearTyping', channelId, userId })

export const upsertResolvedParticipants = (participants: ResolvedParticipant[]): ChatServerAction => ({
  type: 'chatServer/upsertResolvedParticipants',
  participants,
})

/**
 * Declare which user the cached state belongs to. A different uid drops every
 * user-scoped entry, so a session switch cannot leave the previous account's
 * channels, messages or unread counts readable by the next one.
 */
export const setOwner = (uid: string | null): ChatServerAction => ({ type: 'chatServer/setOwner', uid })

export const setConnected = (connected: boolean): ChatServerAction => ({ type: 'chatServer/setConnected', connected })

export const setReady = (ready: boolean): ChatServerAction => ({ type: 'chatServer/setReady', ready })

export const setConnectFailed = (failed: boolean): ChatServerAction => ({ type: 'chatServer/setConnectFailed', failed })

export const setChannelsStatus = (status: ChatServerChannelsStatus): ChatServerAction => ({ type: 'chatServer/setChannelsStatus', status })

export const setArchiveCount = (count: number): ChatServerAction => ({ type: 'chatServer/setArchiveCount', count })

export const chatServerReducer = (state: ChatServerState = initialChatServerState, action: ChatServerAction): ChatServerState => {
  switch (action.type) {
    case 'chatServer/upsertChannels': {
      const channels: Record<string, ChatServerChannel> = {}
      const participants: Record<string, ChatServerParticipant> = {}
      for (const raw of action.raws) {
        const { channel, participants: ps } = convertChannel(raw)
        channels[channel.id] = channel
        for (const p of ps) participants[p.id] = p
      }
      return { ...state, channels: { ...state.channels, ...channels }, participants: { ...state.participants, ...participants } }
    }

    case 'chatServer/upsertChannel': {
      const next = { ...state, channels: { ...state.channels, [action.channel.id]: action.channel } }
      if (!action.participants.length) return next
      const participants = { ...state.participants }
      for (const p of action.participants) participants[p.id] = p
      return { ...next, participants }
    }

    case 'chatServer/removeChannel':
      return { ...state, channels: omitKey(state.channels, action.channelId), messages: omitKey(state.messages, action.channelId) }

    case 'chatServer/upsertMessages':
      return { ...state, messages: { ...state.messages, [action.channelId]: sortMessagesIntoChannel(state.messages[action.channelId], action.messages) } }

    case 'chatServer/addMessage': {
      const { message } = action
      let next: ChatServerState = {
        ...state,
        messages: { ...state.messages, [message.channel_id]: sortMessagesIntoChannel(state.messages[message.channel_id], [message]) },
      }
      // The sidebar orders by channel.updated_at — a live message floats its
      // dialog to the top (mirrors the server-side bump). Advance-only.
      if (message.inserted_at) {
        const channel = next.channels[message.channel_id]
        const messageAt = Date.parse(message.inserted_at)
        if (channel && !(channel.updated_at && Date.parse(channel.updated_at) >= messageAt)) {
          next = { ...next, channels: { ...next.channels, [message.channel_id]: { ...channel, updated_at: message.inserted_at } } }
        }
      }
      // The server auto-unarchives message recipients — mirror it locally so a
      // live message re-surfaces an archived dialog without a reload. Clearing
      // every participant (only self is displayed) is harmless; the one skew —
      // the server keeps a *sender* archived in a self-archived chat — heals on
      // the next full reload.
      const channel = next.channels[message.channel_id]
      if (channel) {
        let participants: Record<string, ChatServerParticipant> | null = null
        for (const id of channel.participantIds) {
          const participant = next.participants[id]
          if (!participant?.archived_at) continue
          participants ??= { ...next.participants }
          participants[id] = { ...participant, archived_at: null }
        }
        if (participants) next = { ...next, participants }
      }
      return next
    }

    case 'chatServer/editMessage': {
      const { message } = action
      const channel = state.messages[message.channel_id]
      if (!channel?.[message.id]) return state
      return { ...state, messages: { ...state.messages, [message.channel_id]: { ...channel, [message.id]: { ...channel[message.id], ...message } } } }
    }

    case 'chatServer/patchMessage': {
      const channel = state.messages[action.channelId]
      if (!channel?.[action.messageId]) return state
      return {
        ...state,
        messages: { ...state.messages, [action.channelId]: { ...channel, [action.messageId]: { ...channel[action.messageId], ...action.patch } } },
      }
    }

    case 'chatServer/removeMessage': {
      const channel = state.messages[action.channelId]
      if (!channel?.[action.messageId]) return state
      return { ...state, messages: { ...state.messages, [action.channelId]: omitKey(channel, action.messageId) } }
    }

    case 'chatServer/setMessageReactions': {
      const channel = state.messages[action.channelId]
      const message = channel?.[action.messageId]
      if (!channel || !message) return state
      return {
        ...state,
        messages: { ...state.messages, [action.channelId]: { ...channel, [action.messageId]: { ...message, reactions: action.reactions } } },
      }
    }

    case 'chatServer/applyReactionDelta': {
      const channel = state.messages[action.channelId]
      const message = channel?.[action.messageId]
      if (!channel || !message) return state
      const current = message.reactions ?? []
      const reactions = applyReactionDeltaToList(current, action)
      if (reactions === current) return state
      return {
        ...state,
        messages: { ...state.messages, [action.channelId]: { ...channel, [action.messageId]: { ...message, reactions } } },
      }
    }

    case 'chatServer/setUnread':
      return { ...state, unread: { ...state.unread, [action.channelId]: action.count } }

    case 'chatServer/incrementUnread':
      return { ...state, unread: { ...state.unread, [action.channelId]: (state.unread[action.channelId] ?? 0) + 1 } }

    case 'chatServer/setUnreadBulk':
      return { ...state, unread: { ...state.unread, ...action.counts } }

    case 'chatServer/applyReadReceipt': {
      // Patch the matching participant's last_read_at from a live `mark_read`
      // WS event, keyed by channel_id + internal user_id. Advance-only.
      const next = Date.parse(action.lastReadAt)
      if (Number.isNaN(next)) return state
      let changed = false
      const participants = { ...state.participants }
      for (const [id, participant] of Object.entries(state.participants)) {
        if (participant.channel_id !== action.channelId || String(participant.user_id) !== String(action.userId)) continue
        const previous = participant.last_read_at ? Date.parse(participant.last_read_at) : 0
        if (next <= previous) continue
        participants[id] = { ...participant, last_read_at: action.lastReadAt }
        changed = true
      }
      return changed ? { ...state, participants } : state
    }

    case 'chatServer/setGlobalPresence':
      return { ...state, globalPresence: action.presence }

    case 'chatServer/applyGlobalPresenceDiff': {
      const current = { ...state.globalPresence }
      for (const [userId, meta] of Object.entries(action.diff.joins ?? {})) current[userId] = meta
      for (const userId of Object.keys(action.diff.leaves ?? {})) delete current[userId]
      return { ...state, globalPresence: current }
    }

    case 'chatServer/setTyping':
      return { ...state, typing: { ...state.typing, [action.channelId]: { ...state.typing[action.channelId], [action.userId]: action.expiresAt } } }

    case 'chatServer/clearTyping': {
      const channel = state.typing[action.channelId]
      if (!channel || !(action.userId in channel)) return state
      return { ...state, typing: { ...state.typing, [action.channelId]: omitKey(channel, action.userId) } }
    }

    case 'chatServer/upsertResolvedParticipants': {
      if (!action.participants.length) return state
      const resolved = { ...state.resolved }
      for (const p of action.participants) resolved[p.external_id] = p
      return { ...state, resolved }
    }

    case 'chatServer/setOwner': {
      if (state.ownerUid === action.uid) return state
      // `connected` / `ready` / `connectFailed` describe the socket, which survives a
      // session switch (the token rotates in-band), so they are carried over.
      return { ...initialChatServerState, ownerUid: action.uid, connected: state.connected, ready: state.ready, connectFailed: state.connectFailed }
    }

    case 'chatServer/setConnected':
      return { ...state, connected: action.connected }

    case 'chatServer/setReady':
      return { ...state, ready: action.ready }

    case 'chatServer/setConnectFailed':
      return state.connectFailed === action.failed ? state : { ...state, connectFailed: action.failed }

    case 'chatServer/setChannelsStatus':
      return { ...state, channelsStatus: action.status }

    case 'chatServer/setArchiveCount':
      return { ...state, archiveCount: action.count }

    default:
      return state
  }
}

const sortMessagesIntoChannel = (current: Record<string, ChatServerMessage> | undefined, messages: ChatServerMessage[]): Record<string, ChatServerMessage> => {
  const next = { ...(current ?? {}) }
  for (const message of messages) {
    if (message.clientId) {
      // Reconcile an optimistic message with its server echo.
      for (const [key, existing] of Object.entries(next)) {
        if (existing.clientId && existing.clientId === message.clientId) delete next[key]
      }
    }
    const previous = next[message.id]
    next[message.id] = previous ? { ...previous, ...message } : message
  }
  return next
}

const omitKey = <T>(obj: Record<string, T>, key: string): Record<string, T> => {
  if (!(key in obj)) return obj
  const next = { ...obj }
  delete next[key]
  return next
}

const applyReactionDeltaToList = (
  reactions: ChatServerReaction[],
  { emoji, actorExternalId, direction, countAfter }: { emoji: string; actorExternalId: string; direction: 'added' | 'removed'; countAfter: number | null }
): ChatServerReaction[] => {
  const existing = reactions.find(reaction => reaction.emoji === emoji)
  if (direction === 'added') {
    const already = existing?.reacted_by.includes(actorExternalId) ?? false
    const entry: ChatServerReaction = {
      emoji,
      count: countAfter ?? (existing ? existing.count + (already ? 0 : 1) : 1),
      reacted_by: existing ? (already ? existing.reacted_by : [...existing.reacted_by, actorExternalId]) : [actorExternalId],
    }
    // New emoji appends at the end — matches the server's first-inserted_at ordering.
    return existing ? reactions.map(reaction => (reaction.emoji === emoji ? entry : reaction)) : [...reactions, entry]
  }
  if (!existing) return reactions
  const count = countAfter ?? existing.count - (existing.reacted_by.includes(actorExternalId) ? 1 : 0)
  if (count <= 0) return reactions.filter(reaction => reaction.emoji !== emoji)
  return reactions.map(reaction =>
    reaction.emoji === emoji ? { emoji, count, reacted_by: existing.reacted_by.filter(id => id !== actorExternalId) } : reaction
  )
}
